import React, { useState, useEffect } from 'react';
import {
  Dialog,
  IconButton,
  CircularProgress,
  Autocomplete,
  TextField,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import InboxIcon from '@mui/icons-material/Inbox';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';
import { 
  ListPosShifReportSalesAPI,
  ListPosPaymentSummaryAPI 
} from '../../../component/Modules/PointOfSale/Report/ReportAPI';
import { viewShiftAPICall } from '../../../API/Settings/Shift/viewShiftAPI';
import './mobileReports.css';

const MobileShiftSummary = ({ open, onClose, branchId: propBranchId, branchName: propBranchName }) => {
  const [loading, setLoading] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftDetails, setShiftDetails] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    sales: true,
    payments: true
  });

  const decimalPosition = Number(localStorage.getItem("decimalPosition")) || 2;
  const userInfo = useSelector((state) => state.loginResponseSlice.value);
  const shiftStatus = useSelector((state) => state.shiftStatusSlice.value);
  const allShiftList = useSelector((state) => state.viewShiftSlice.value);
  const { shiftReportSale, shiftReportPaymentSummary } = useSelector((state) => state.posReportshiftSlice);

  // Use prop branchId if provided, otherwise fall back to userInfo
  const effectiveBranchId = propBranchId || userInfo?.branchPk;

  useEffect(() => {
    if (open && effectiveBranchId) {
      viewShiftAPICall({ branchId: effectiveBranchId });
      setSelectedShift(null); // Reset shift when branch changes
      
      // Auto-select current shift if available and no prop branchId
      if (!propBranchId && shiftStatus?._id) {
        setSelectedShift(shiftStatus);
        fetchShiftData(shiftStatus._id);
      }
    }
  }, [open, effectiveBranchId]);

  const fetchShiftData = async (shiftId) => {
    if (!shiftId || !effectiveBranchId) return;
    
    setLoading(true);
    try {
      const body = {
        branchId: effectiveBranchId,
        shiftId: shiftId,
        index: 0
      };
      await Promise.all([
        ListPosShifReportSalesAPI(body, setShiftDetails),
        ListPosPaymentSummaryAPI(body)
      ]);
    } catch (error) {
      console.error('Error fetching shift data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShiftChange = (event, newValue) => {
    setSelectedShift(newValue);
    if (newValue?._id) {
      fetchShiftData(newValue._id);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toFixed(decimalPosition);
  };

  const renderSalesSummary = () => {
    const sales = shiftReportSale?.sale;
    if (!sales?.length) return null;

    const totalAmount = sales.reduce((sum, order) => sum + Number(order.amtTotal || 0), 0);
    const totalPaid = sales.reduce((sum, order) => sum + Number(order.paidAmt || 0), 0);
    const totalBalance = sales.reduce((sum, order) => sum + Number(order.amountDue || 0), 0);

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header sales"
          onClick={() => toggleSection('sales')}
        >
          <h4><ReceiptLongIcon /> Sales ({sales.length} orders)</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(totalAmount)}</span>
            {expandedSections.sales ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.sales ? 'expanded' : ''}`}>
          <table className="mobile-report-table">
            <thead>
              <tr>
                <th>Order</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Paid</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((order, idx) => (
                <tr key={idx}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{order.name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#666' }}>{order.customerName || 'Walk-in'}</div>
                  </td>
                  <td className="text-right">{formatAmount(order.amtTotal)}</td>
                  <td className="text-right" style={{ color: '#11998e' }}>{formatAmount(order.paidAmt)}</td>
                  <td className="text-right" style={{ color: order.amountDue > 0 ? '#f5576c' : '#666' }}>{formatAmount(order.amountDue)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Total ({sales.length})</td>
                <td className="text-right">{formatAmount(totalAmount)}</td>
                <td className="text-right">{formatAmount(totalPaid)}</td>
                <td className="text-right">{formatAmount(totalBalance)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPaymentSummary = () => {
    if (!shiftReportPaymentSummary?.length) return null;

    const total = shiftReportPaymentSummary.reduce((sum, p) => sum + Number(p.amount || 0), 0);

    return (
      <div className="mobile-report-card">
        <div 
          className="mobile-report-card-header payment"
          onClick={() => toggleSection('payments')}
        >
          <h4><PaymentsIcon /> Payment Summary</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="total-badge">{formatAmount(total)}</span>
            {expandedSections.payments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </div>
        <div className={`mobile-report-card-content ${expandedSections.payments ? 'expanded' : ''}`}>
          <table className="mobile-report-table">
            <thead>
              <tr>
                <th>Payment Method</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {shiftReportPaymentSummary.map((payment, idx) => (
                <tr key={idx}>
                  <td>{payment._id || payment.type}</td>
                  <td className="text-right">{formatAmount(payment.amount)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Total</td>
                <td className="text-right">{formatAmount(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const renderShiftInfo = () => {
    if (!selectedShift) return null;

    const shiftData = shiftReportSale?.shiftData || selectedShift;
    const isActive = !shiftData?.endDate;

    return (
      <div className="shift-info-banner">
        <div className="shift-id">
          Shift {shiftData?.shiftSeq || shiftData?.shiftId || selectedShift?.shiftSeq}
          {isActive && <span style={{ marginLeft: 8, padding: '2px 8px', backgroundColor: '#11998e', color: 'white', borderRadius: 4, fontSize: '0.7rem' }}>ACTIVE</span>}
        </div>
        <div className="shift-details" style={{ flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ color: '#666' }}>Started by:</span>
            <span>{shiftData?.startedBy || selectedShift?.employeeName || userInfo?.userName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ color: '#666' }}>Start:</span>
            <span>{formatDateTime(shiftData?.startDate || selectedShift?.startDate)}</span>
          </div>
          {shiftData?.endDate && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ color: '#666' }}>Ended by:</span>
                <span>{shiftData?.endedBy || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ color: '#666' }}>End:</span>
                <span>{formatDateTime(shiftData?.endDate)}</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="mobile-report-dialog"
    >
      <div className="mobile-report-header">
        <h3>Shift Report</h3>
        <IconButton className="close-btn" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <div className="mobile-report-container">
        {/* Shift Selector */}
        <div className="mobile-report-filters">
          <Autocomplete
            fullWidth
            size="small"
            options={allShiftList || []}
            getOptionLabel={(option) => `Shift ${option.shiftSeq || option.shiftId} - ${option.startDate || 'Active'}`}
            value={selectedShift}
            onChange={handleShiftChange}
            renderInput={(params) => (
              <TextField {...params} label="Select Shift" />
            )}
          />
        </div>

        {loading ? (
          <div className="mobile-report-loading">
            <CircularProgress size={40} />
            <p>Loading shift data...</p>
          </div>
        ) : !selectedShift ? (
          <div className="mobile-report-empty">
            <InboxIcon />
            <p>Select a shift to view report.</p>
          </div>
        ) : (
          <>
            {renderShiftInfo()}
            {renderSalesSummary()}
            {renderPaymentSummary()}
            
            {!shiftReportSale?.sale?.length && !shiftReportPaymentSummary?.length && (
              <div className="mobile-report-empty">
                <InboxIcon />
                <p>No data available for this shift.</p>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};

export default MobileShiftSummary;
